/**
 * Zod schemas mirroring the Lambda pydantic models.
 *
 * Per `api-standards.md` (Python ↔ TypeScript contract):
 *   - Python `Optional[X]` returned in JSON as `null` MUST be `.nullable()` here.
 *   - Python fields stripped via `exclude_none=True` would be `.optional()` —
 *     but our Lambdas always return the full shape (no `exclude_none`), so the
 *     defensive choice across the board is `.nullable()`.
 *
 * These schemas are framework-agnostic — usable by the web app today and by a
 * future React Native client tomorrow without modification.
 */

import { z } from "zod";

// --------------------------------------------------------------------------- //
// Common envelopes                                                            //
// --------------------------------------------------------------------------- //

export const ApiMetaSchema = z.object({
  requestId: z.string(),
  timestamp: z.string(),
});

export const ApiErrorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z
      .array(z.object({ field: z.string(), issue: z.string() }))
      .nullable()
      .optional(),
  }),
  meta: ApiMetaSchema,
});
export type ApiErrorBody = z.infer<typeof ApiErrorBodySchema>;

export const apiSuccess = <T extends z.ZodTypeAny>(
  data: T,
): z.ZodObject<{
  data: T;
  meta: typeof ApiMetaSchema;
}> =>
  z.object({
    data,
    meta: ApiMetaSchema,
  });

export const cursorList = <T extends z.ZodTypeAny>(
  item: T,
): z.ZodObject<{
  items: z.ZodArray<T>;
  nextCursor: z.ZodNullable<z.ZodString>;
}> =>
  z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });

// --------------------------------------------------------------------------- //
// Profile                                                                     //
// --------------------------------------------------------------------------- //

export const SexSchema = z.enum([
  "female",
  "male",
  "intersex",
  "prefer_not_to_say",
]);
export const UnitsSchema = z.enum(["metric", "imperial"]);

export const ProfileSchema = z.object({
  name: z.string().nullable(),
  sex: SexSchema.nullable(),
  gender: z.string().nullable(),
  birthdate: z.string().nullable(),
  heightCm: z.number().nullable(),
  weightKg: z.number().nullable(),
  personalHistory: z.string().nullable(),
  familyHistory: z.string().nullable(),
  units: UnitsSchema.nullable(),
  timezone: z.string().nullable(),
  allergyFish: z.boolean().nullable(),
  allergyShellfish: z.boolean().nullable(),
  seafoodAversion: z.boolean().nullable(),
  aversions: z.array(z.string()).nullable(),
  dietaryPattern: z.string().nullable(),
  latitude: z.number().nullable(),
  existingSupplements: z.array(z.string()).nullable(),
  completeness: z.number(),
  updatedAt: z.string().nullable(),
});
export type ProfileT = z.infer<typeof ProfileSchema>;

export const ProfileUpsertSchema = z.object({
  name: z.string().max(200).optional().nullable(),
  sex: SexSchema.optional().nullable(),
  gender: z.string().max(50).optional().nullable(),
  birthdate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "ISO YYYY-MM-DD required")
    .optional()
    .nullable(),
  heightCm: z.number().gt(0).lt(300).optional().nullable(),
  weightKg: z.number().gt(0).lt(700).optional().nullable(),
  personalHistory: z.string().max(5000).optional().nullable(),
  familyHistory: z.string().max(5000).optional().nullable(),
  units: UnitsSchema.optional().nullable(),
  timezone: z.string().max(64).optional().nullable(),
  allergyFish: z.boolean().optional().nullable(),
  allergyShellfish: z.boolean().optional().nullable(),
  seafoodAversion: z.boolean().optional().nullable(),
  aversions: z.array(z.string()).max(50).optional().nullable(),
  dietaryPattern: z
    .enum([
      "omnivore",
      "vegetarian",
      "vegan",
      "pescatarian",
      "keto",
      "mediterranean",
      "other",
    ])
    .optional()
    .nullable(),
  latitude: z.number().min(-90).max(90).optional().nullable(),
  existingSupplements: z.array(z.string()).max(100).optional().nullable(),
});
export type ProfileUpsertT = z.infer<typeof ProfileUpsertSchema>;

// --------------------------------------------------------------------------- //
// Symptoms                                                                    //
// --------------------------------------------------------------------------- //

export const SymptomStatusSchema = z.enum(["active", "resolved"]);

export const SymptomSchema = z.object({
  id: z.string(),
  name: z.string(),
  severity: z.number().int().min(0).max(10).nullable(),
  onset: z.string().nullable(),
  duration: z.string().nullable(),
  frequency: z.string().nullable(),
  location: z.string().nullable(),
  triggers: z.array(z.string()).nullable(),
  notes: z.string().nullable(),
  status: SymptomStatusSchema,
  updatedAt: z.string().nullable().optional(),
});
export type SymptomT = z.infer<typeof SymptomSchema>;

export const SymptomCreateSchema = z.object({
  name: z.string().min(1).max(200),
  severity: z.number().int().min(0).max(10).optional().nullable(),
  onset: z.string().max(200).optional().nullable(),
  duration: z.string().max(200).optional().nullable(),
  frequency: z.string().max(200).optional().nullable(),
  location: z.string().max(200).optional().nullable(),
  triggers: z.array(z.string()).max(20).optional().nullable(),
  notes: z.string().max(5000).optional().nullable(),
});
export type SymptomCreateT = z.infer<typeof SymptomCreateSchema>;

export const SymptomUpdateSchema = SymptomCreateSchema.partial().extend({
  status: SymptomStatusSchema.optional(),
});
export type SymptomUpdateT = z.infer<typeof SymptomUpdateSchema>;

// --------------------------------------------------------------------------- //
// Health facts (optional inputs)                                              //
// --------------------------------------------------------------------------- //

/**
 * Wire schema for a single optional-health-input fact (e.g. a medication,
 * allergy, immunization). Backed by ``/api/profile/facts``.
 *
 * Per ``api-standards.md`` Python ↔ TypeScript contract: the Lambda always
 * emits ``value``/``unit``/``notes`` as ``null`` when unset (no
 * ``exclude_none``), so they MUST be ``.nullable()`` here. ``id`` /
 * ``category`` / ``label`` / ``createdAt`` / ``updatedAt`` are always
 * present strings.
 */
export const FactSchema = z.object({
  id: z.string(),
  category: z.string(),
  label: z.string(),
  value: z.string().nullable(),
  unit: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FactT = z.infer<typeof FactSchema>;

export const FactCreateSchema = z.object({
  category: z.string().min(1).max(64),
  label: z.string().min(1).max(200),
  value: z.string().max(500).optional().nullable(),
  unit: z.string().max(64).optional().nullable(),
  notes: z.string().max(2000).optional().nullable(),
});
export type FactCreateT = z.infer<typeof FactCreateSchema>;

/** Response shape for ``DELETE /api/profile/facts/{id}``. */
export const FactDeletedSchema = z.object({
  deleted: z.literal(true),
});
export type FactDeletedT = z.infer<typeof FactDeletedSchema>;

// --------------------------------------------------------------------------- //
// Documents                                                                   //
// --------------------------------------------------------------------------- //

export const DocumentCategorySchema = z.enum([
  "lab_result",
  "prescription",
  "imaging",
  "doctors_note",
  "genetic_export",
  "body_photo",
  "other",
]);

export const DocumentStatusSchema = z.enum([
  "pending_upload",
  "processing",
  "ready",
  "failed",
  "queued_for_retry",
]);

const DocumentBase = z.object({
  id: z.string(),
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
  category: DocumentCategorySchema,
  notes: z.string().nullable(),
  s3Key: z.string(),
  status: DocumentStatusSchema,
  summary: z.string().nullable(),
  biomarkerCount: z.number().optional(),
  errorMessage: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});

export const DocumentSchema = DocumentBase.extend({
  uploadUrl: z.string().optional(),
  uploadExpiresIn: z.number().optional(),
  downloadUrl: z.string().optional(),
  downloadExpiresIn: z.number().optional(),
});
export type DocumentT = z.infer<typeof DocumentSchema>;

export const DocumentCreateSchema = z.object({
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(200),
  sizeBytes: z
    .number()
    .int()
    .gt(0)
    .max(50 * 1024 * 1024),
  category: DocumentCategorySchema.default("other"),
  notes: z.string().max(2000).optional().nullable(),
});
export type DocumentCreateT = z.infer<typeof DocumentCreateSchema>;

// --------------------------------------------------------------------------- //
// Passkeys                                                                    //
// --------------------------------------------------------------------------- //

export const PasskeySchema = z.object({
  credentialId: z.string(),
  name: z.string(),
  createdAt: z.string().nullable(),
  authenticatorAttachment: z.string().nullable(),
});
export type PasskeyT = z.infer<typeof PasskeySchema>;

// --------------------------------------------------------------------------- //
// Conversations & messages                                                    //
// --------------------------------------------------------------------------- //

export const ChatModeSchema = z.enum(["smart", "advanced"]);

export const ConversationSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  mode: ChatModeSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ConversationT = z.infer<typeof ConversationSchema>;

export const ChatCitationSchema = z.object({
  source: z.string(),
  url: z.string().nullable(),
});

export const ChatToolUseSchema = z.object({
  name: z.string(),
  detail: z.string().nullable(),
});

export const ChatMessageSchema = z.object({
  id: z.string(),
  conversationId: z.string(),
  role: z.enum(["user", "assistant", "system"]),
  content: z.string(),
  citations: z.array(ChatCitationSchema).default([]),
  toolUses: z.array(ChatToolUseSchema).default([]),
  createdAt: z.string(),
});
export type ChatMessageT = z.infer<typeof ChatMessageSchema>;

/**
 * Wire schema for messages persisted by the conversations Lambda.
 *
 * The Lambda emits ``convId`` (matching the DynamoDB attribute) rather than
 * ``conversationId``. The UI types in ``core/types.ts`` use the longer name
 * for clarity in client code; this wire schema documents the actual API
 * contract so contract drift is detected at the test boundary.
 */
export const PersistedChatMessageWireSchema = z.object({
  id: z.string(),
  convId: z.string(),
  role: z.enum(["user", "assistant", "system", "tool"]),
  content: z.string(),
  citations: z.array(ChatCitationSchema).default([]),
  toolUses: z
    .array(
      z.object({
        name: z.string(),
        id: z.string().nullable().optional(),
      }),
    )
    .default([]),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  userId: z.string().nullable().optional(),
});
export type PersistedChatMessageWireT = z.infer<
  typeof PersistedChatMessageWireSchema
>;

/** Response shape for ``GET /api/conversations/{id}``. */
export const ConversationGetResponseSchema = z.object({
  conversation: ConversationSchema.passthrough(),
  messages: z.array(PersistedChatMessageWireSchema.passthrough()),
  nextCursor: z.string().nullable(),
});
export type ConversationGetResponseT = z.infer<
  typeof ConversationGetResponseSchema
>;

/** Response shape for ``DELETE /api/account``. */
export const AccountDeleteResponseSchema = z.object({
  deletedDdbItems: z.number().int().nonnegative(),
  deletedS3Objects: z.number().int().nonnegative(),
  deletedVectors: z.number().int().nonnegative(),
});
export type AccountDeleteResponseT = z.infer<
  typeof AccountDeleteResponseSchema
>;

/** Response shape for ``GET /api/auth/passkeys``. */
export const PasskeyListResponseSchema = z.object({
  items: z.array(PasskeySchema),
});
export type PasskeyListResponseT = z.infer<typeof PasskeyListResponseSchema>;

// --------------------------------------------------------------------------- //
// Dashboard                                                                   //
// --------------------------------------------------------------------------- //

export const BiomarkerPointSchema = z.object({
  t: z.string(),
  value: z.number(),
});

export const BiomarkerSeriesSchema = z.object({
  code: z.string(),
  unit: z.string().nullable(),
  referenceRange: z.string().nullable(),
  points: z.array(BiomarkerPointSchema),
});

export const AiHighlightSchema = z.object({
  title: z.string(),
  detail: z.string(),
});

export const DashboardSchema = z.object({
  profile: ProfileSchema.partial().nullable(),
  activeSymptoms: z.array(SymptomSchema),
  recentDocuments: z.array(DocumentSchema),
  biomarkers: z.array(BiomarkerSeriesSchema),
  aiHighlights: z.array(AiHighlightSchema),
  meds: z.array(z.string()).optional(),
  allergies: z.array(z.string()).optional(),
});
export type DashboardT = z.infer<typeof DashboardSchema>;

// --------------------------------------------------------------------------- //
// Full Analysis (recommendations)                                             //
// --------------------------------------------------------------------------- //

export const RecommendationPrioritySchema = z.enum(["high", "medium", "low"]);

export const RecommendationSchema = z.object({
  id: z.string(),
  category: z.string(),
  priority: RecommendationPrioritySchema,
  title: z.string(),
  rationale: z.string(),
  expectedOutcome: z.string(),
  detail: z.string().nullable(),
});
export type RecommendationT = z.infer<typeof RecommendationSchema>;

export const AnalysisStatusSchema = z.enum(["pending", "ready", "failed"]);

export const AnalysisSchema = z.object({
  id: z.string(),
  version: z.number().int(),
  status: AnalysisStatusSchema,
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable().optional(),
  recommendations: z.array(RecommendationSchema).default([]),
  errorMessage: z.string().nullable().optional(),
});
export type AnalysisT = z.infer<typeof AnalysisSchema>;

export const AnalysisListItemSchema = z.object({
  version: z.number().int(),
  status: AnalysisStatusSchema,
  createdAt: z.string().nullable(),
  count: z.number().int(),
});
export const AnalysisListSchema = z.object({
  items: z.array(AnalysisListItemSchema),
  nextCursor: z.string().nullable(),
});
export type AnalysisListT = z.infer<typeof AnalysisListSchema>;

// --------------------------------------------------------------------------- //
// Genetic Health Protocol                                                     //
// --------------------------------------------------------------------------- //

export const ProtocolStatusSchema = z.enum(["pending", "ready", "failed"]);
const _s = z.string().nullable().optional();

export const ProtocolSupplementSchema = z
  .object({
    name: _s,
    form: _s,
    dose: _s,
    timing: _s,
    reason: _s,
  })
  .passthrough();
export const ProtocolTiersSchema = z.object({
  tier1: z.array(ProtocolSupplementSchema).default([]),
  tier2: z.array(ProtocolSupplementSchema).default([]),
  tier3: z.array(ProtocolSupplementSchema).default([]),
});
export const ProtocolScheduleSchema = z.object({
  morning: z.array(z.record(z.string(), z.unknown())).default([]),
  lunch: z.array(z.record(z.string(), z.unknown())).default([]),
  dinner: z.array(z.record(z.string(), z.unknown())).default([]),
  bedtime: z.array(z.record(z.string(), z.unknown())).default([]),
});
export const CabinetItemSchema = z
  .object({
    name: _s,
    brand: _s,
    dose: _s,
    verdict: _s,
    reason: _s,
    covers: _s,
  })
  .passthrough();
export const ShoppingItemSchema = z
  .object({ name: _s, form: _s, dose: _s, tier: _s })
  .passthrough();

export const ProtocolSchema = z.object({
  id: z.string().optional(),
  subjectId: z.string().optional(),
  version: z.number().int(),
  status: ProtocolStatusSchema,
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  apoeType: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),
  findings: z.array(z.record(z.string(), z.unknown())).default([]),
  interactions: z
    .array(z.object({ name: _s, finding: _s }).passthrough())
    .default([]),
  tiers: ProtocolTiersSchema.optional(),
  doNotTake: z
    .array(z.object({ substance: _s, reason: _s }).passthrough())
    .default([]),
  nutrition: z.string().nullable().optional(),
  exercise: z.string().nullable().optional(),
  mealGuide: z.record(z.string(), z.array(z.string())).nullable().optional(),
  schedule: ProtocolScheduleSchema.optional(),
  shoppingList: z.array(ShoppingItemSchema).default([]),
  cabinet: z.array(CabinetItemSchema).default([]),
  cabinetStatus: z.string().nullable().optional(),
  labsToRequest: z.array(z.string()).default([]),
  errorMessage: z.string().nullable().optional(),
});
export type ProtocolT = z.infer<typeof ProtocolSchema>;

export const ProtocolSummarySchema = z.object({
  version: z.number().int(),
  status: ProtocolStatusSchema,
  createdAt: z.string().nullable(),
  apoeType: z.string().nullable().optional(),
});
export const ProtocolListSchema = z.object({
  items: z.array(ProtocolSummarySchema),
  nextCursor: z.string().nullable(),
});
export type ProtocolListT = z.infer<typeof ProtocolListSchema>;

// --------------------------------------------------------------------------- //
// Daily tracking + shopping advisor                                           //
// --------------------------------------------------------------------------- //

export const MacrosSchema = z
  .object({
    kcal: z.number().default(0),
    protein: z.number().default(0),
    carb: z.number().default(0),
    fat: z.number().default(0),
    satFat: z.number().default(0),
    fiber: z.number().default(0),
    sugar: z.number().default(0),
  })
  .partial()
  .passthrough();
export const SupplementLogSchema = z
  .object({
    type: z.string().optional(),
    name: z.string().nullable().optional(),
    scheduleItemId: z.string().nullable().optional(),
    taken: z.boolean().optional(),
    ts: z.string().optional(),
  })
  .passthrough();
export const MealLogSchema = z
  .object({
    type: z.string().optional(),
    text: z.string().nullable().optional(),
    items: z.array(z.string()).default([]),
    macros: MacrosSchema.optional(),
    verdict: z.string().nullable().optional(),
    why: z.string().nullable().optional(),
    suggestions: z.array(z.string()).default([]),
    photoKey: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    ts: z.string().optional(),
  })
  .passthrough();
export const TrackingDaySchema = z.object({
  date: z.string(),
  targets: z.record(z.string(), z.unknown()).default({}),
  supplements: z.array(SupplementLogSchema).default([]),
  meals: z.array(MealLogSchema).default([]),
  macroTotals: MacrosSchema,
  adherence: z.object({
    taken: z.number().int(),
    total: z.number().int(),
    pct: z.number().int(),
  }),
  proteinStreakDays: z.number().int().default(0),
});
export type TrackingDayT = z.infer<typeof TrackingDaySchema>;

export const ShoppingAdviceSchema = z
  .object({
    type: z.string().optional(),
    productName: z.string().nullable().optional(),
    product: z.string().nullable().optional(),
    nutrition: z.record(z.string(), z.unknown()).nullable().optional(),
    ingredients: z.array(z.string()).default([]),
    verdict: z.string().nullable().optional(),
    why: z.string().nullable().optional(),
    alternative: z.string().nullable().optional(),
    photoKey: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    ts: z.string().optional(),
  })
  .passthrough();
export const ShoppingHistorySchema = z.object({
  items: z.array(ShoppingAdviceSchema),
  nextCursor: z.string().nullable(),
});
export type ShoppingAdviceT = z.infer<typeof ShoppingAdviceSchema>;

export const CoachPhotoSchema = z.object({
  uploadUrl: z.string(),
  photoKey: z.string(),
});

export const BiomarkerEntrySchema = z.object({
  code: z.string(),
  value: z.number(),
  unit: z.string(),
  referenceRange: z.string().nullable(),
  takenAt: z.string().nullable(),
  source: z.string(),
  key: z.string(),
});
export type BiomarkerEntryT = z.infer<typeof BiomarkerEntrySchema>;
export const BiomarkerListSchema = z.object({
  items: z.array(BiomarkerEntrySchema),
  nextCursor: z.string().nullable(),
});
export const BiomarkerCreateSchema = z.object({
  code: z.string().min(1).max(40),
  value: z.number(),
  unit: z.string().max(40).optional().nullable(),
  referenceRange: z.string().max(80).optional().nullable(),
  takenAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "ISO YYYY-MM-DD required")
    .optional()
    .nullable(),
});
export type BiomarkerCreateT = z.infer<typeof BiomarkerCreateSchema>;

// --------------------------------------------------------------------------- //
// WebSocket frames                                                            //
// --------------------------------------------------------------------------- //

export const WsFrameSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("token"), text: z.string() }),
  z.object({
    type: z.literal("tool"),
    name: z.string(),
    detail: z.string().nullable().optional(),
  }),
  z.object({
    type: z.literal("citation"),
    source: z.string(),
    url: z.string().nullable().optional(),
  }),
  z.object({ type: z.literal("title"), title: z.string() }),
  z.object({ type: z.literal("done"), messageId: z.string() }),
  z.object({ type: z.literal("error"), message: z.string() }),
  z.object({
    type: z.literal("documentStatus"),
    docId: z.string(),
    status: z.string(),
  }),
  z.object({
    type: z.literal("analysisStatus"),
    version: z.number().int(),
    status: z.string(),
  }),
  z.object({
    type: z.literal("protocolStatus"),
    version: z.number().int(),
    status: z.string(),
  }),
  z.object({
    type: z.literal("trackingStatus"),
    logId: z.string(),
    status: z.string(),
  }),
]);
export type WsFrame = z.infer<typeof WsFrameSchema>;

export const WsClientFrameSchema = z.object({
  action: z.literal("sendMessage"),
  conversationId: z.string(),
  content: z.string(),
  mode: ChatModeSchema,
  imageKeys: z.array(z.string()).optional(),
});
export type WsClientFrame = z.infer<typeof WsClientFrameSchema>;
